import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import { DatasetActions, datasetsSelector } from 'Redux/thunks/datasets.thunks';
import { PipeActions } from 'Redux/thunks/pipes.thunks';
import { SystemActions } from 'Redux/thunks/systems.thunks';

import OverviewInstructions from '../../../components/instructions-splash/instructions-overview';
import { pipesSelector, systemsSelector } from 'Redux/selectors';
import SesamHub from '../../components/hub/SesamHub';
import { Flows } from 'Internals/flows';

import { LoadingPanel } from 'Common/LoadingPanel';
import { groupSystems, isNotInternal } from './overviewUtils';
import { SubscriptionOverview } from './SubscriptionOverview.view';
import Page from '../../../components/page';

import {
  DownstreamMap,
  InboundMap,
  LookupMap,
  OutboundMap,
  TransformMap,
} from 'Types/common.types';
import { useSystemNodes } from 'Hooks/useSystemNodes';
import { useOverviewDatasets } from 'Hooks/useOverviewDatasets';

import type { RouteComponentProps } from 'react-router';
import type { RootState } from 'Types/state.types';
import type { Dataset } from 'Types/dataset.types';
import type { PipeMap } from 'Types/pipes.types';
import type { System } from 'Types/system.types';
import type { Flow } from '../../../types/flows.types';

import './OverviewStyle.css';

export const NODE_SYSTEM = 'system:sesam-node:sesam:node';

type OverviewProps = {
  datasets: Dataset[];
  pipes: PipeMap;
  downstreams: DownstreamMap;
  lookups: LookupMap;
  inbounds: InboundMap;
  outbounds: OutboundMap;
  subId: string;
  systems: System[];
  hasUserDatasets: boolean;
  hasUserPipes: boolean;
  hasUserSystems: boolean;
  darkModeActive: boolean;
  transforms: TransformMap;
  context: {
    router: any;
  };
};

const Overview: React.FC<RouteComponentProps<OverviewProps, any> & OverviewProps> = (props) => {
  const {
    context,
    datasets,
    pipes,
    downstreams,
    lookups,
    inbounds,
    outbounds,
    subId,
    systems,
    hasUserDatasets,
    hasUserPipes,
    hasUserSystems,
    darkModeActive,
    transforms,
  } = props;

  const dispatch = useDispatch();

  const onDatasetClick = () => {
    context.router.push(`/subscription/${subId}/pipes`);
  };

  const onInternalPipesClick = () => {
    context.router.push(
      `/subscription/${subId}/pipes?config.effective.source.system=${NODE_SYSTEM}&config.effective.sink.system=${NODE_SYSTEM}`
    );
  };

  const onSystemClick = () => {
    context.router.push(`/subscription/${subId}/systems`);
  };

  const [flows, setFlows] = useState<Flow[]>();

  const loadEverything = () =>
    Promise.all([
      dispatch(DatasetActions.loadAllForced()),
      dispatch(PipeActions.loadAllForced()),
      dispatch(SystemActions.loadAllForced()),
    ]);

  const everythingLoaded = useSelector((state: RootState) => {
    return (
      state.loadStatus.datasetsLoaded &&
      state.loadStatus.pipesLoaded &&
      state.loadStatus.systemsLoaded
    );
  });

  useEffect(() => {
    Flows.get(pipes, downstreams, lookups).then((flows) => {
      setFlows(flows);
    });
  }, [pipes, downstreams, lookups]);

  const filteredSystemNodes = systems.filter(isNotInternal).reduce(groupSystems, {});
  const filteredSystemNodesArray = Object.values(filteredSystemNodes);
  const systemNodes = useSystemNodes({
    systemNodes: filteredSystemNodesArray,
    pipes,
    inbounds,
    outbounds,
    transforms,
    darkModeActive,
    flows,
    subId,
  });

  const { sumLatest, sumDeleted, sumHistory } = useOverviewDatasets(datasets);

  useEffect(() => {
    loadEverything();
  }, []);

  if (!everythingLoaded) {
    return <LoadingPanel />;
  }

  if (!hasUserDatasets || !hasUserPipes) {
    return (
      // @ts-ignore
      <Page>
        <OverviewInstructions
          subId={subId}
          hasUserDatasets={hasUserDatasets}
          hasUserPipes={hasUserPipes}
          hasUserSystems={hasUserSystems}
        />
      </Page>
    );
  }

  return (
    <SubscriptionOverview>
      <SesamHub
        sumLatest={sumLatest}
        sumDeleted={sumDeleted}
        sumHistory={sumHistory}
        onDatasetClick={onDatasetClick}
        onPipeClick={onInternalPipesClick}
        onSystemClick={onSystemClick}
        subscriptionId={subId}
        systems={systemNodes}
        darkModeActive={darkModeActive}
      />
    </SubscriptionOverview>
  );
};

function mapStateToProps(state: RootState) {
  return {
    datasets: datasetsSelector(state),
    everythingLoaded:
      state.loadStatus.datasetsLoaded &&
      state.loadStatus.pipesLoaded &&
      state.loadStatus.systemsLoaded,
    hasUserDatasets:
      state.loadStatus.datasetsLoaded &&
      datasetsSelector(state).some((dataset) => !dataset.isInternal),
    hasUserPipes:
      state.loadStatus.pipesLoaded && pipesSelector(state).some((pipe) => !pipe.isInternal),
    hasUserSystems:
      state.loadStatus.systemsLoaded && systemsSelector(state).some((system) => !system.isInternal),
    pipes: state.pipes,
    downstreams: state.downstreams,
    lookups: state.lookups,
    inbounds: state.inbounds,
    outbounds: state.outbounds,
    subId: state.subscription.id,
    systems: systemsSelector(state),
    darkModeActive: state.theme.dark,
    transforms: state.transforms,
  };
}

export default connect(mapStateToProps)(Overview);
